/* Enhance Kalles' existing drawer; keep its dialog, scroll lock and utility drawers. */
class ODSMobileNavigation extends HTMLElement {
  async connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    await customElements.whenDefined('hdt-drawer');
    if (signal.aborted || !this.isConnected) return;
    this.dialog = this.closest('dialog');
    this.drawer = this.dialog.parentElement;
    this.section = this.closest('.shopify-section');
    this.scrollArea = this.querySelector('.ods-mobile-navigation__scroll');
    this.mobile = matchMedia('(max-width: 1149px)');
    this.stack = [];
    this.dialog.addEventListener('dialog:opening', () => this.opened(), { signal });
    this.dialog.addEventListener('dialog:closing', () => this.cancelPanelMotion(), { signal });
    this.dialog.addEventListener('dialog:close', () => this.closed(), { signal });
    this.addEventListener('click', event => this.navigate(event), { signal });
    this.dialog.addEventListener('keydown', event => this.containFocus(event), { signal });
    document.addEventListener('click', event => this.transition(event), { capture: true, signal });
    this.mobile.addEventListener('change', () => {
      if (!this.mobile.matches && this.dialog.open) this.drawer.close(true);
    }, { signal });
    this.observer = new MutationObserver(() => this.enhance());
    this.observer.observe(this.scrollArea, { childList: true, subtree: true });
    this.enhance();
    if (this.dialog.open) this.opened();
  }

  enhance() {
    const menu = this.querySelector('[data-ods-menu]');
    if (!menu || menu.dataset.enhanced || !menu.querySelector('[data-ods-root]')) return;
    menu.dataset.enhanced = 'true';
    this.menu = menu;
    this.root = menu.querySelector('[data-ods-root]');
    // Flatten nested panels once, including markup returned by Kalles lazy submenu loading.
    menu.querySelectorAll('[data-ods-panel]').forEach(panel => {
      panel.hidden = true;
      menu.append(panel);
    });
    menu.querySelectorAll('[data-ods-parent-link]').forEach(link => { link.hidden = true; });
    menu.querySelectorAll('[data-ods-next], [data-ods-back]').forEach(button => { button.hidden = false; });
    this.current = this.root;
  }

  opened() {
    if (!this.mobile.matches) { this.drawer.close(true); return; }
    this.pageScroll = window.scrollY;
    this.trigger = this.dialog.btnOpening;
    this.row = this.section?.querySelector('hdt-sticky-header [data-header-height]');
    if (this.row) {
      const rect = this.row.getBoundingClientRect();
      this.marker = document.createElement('div');
      this.marker.style.height = `${rect.height}px`;
      this.row.before(this.marker);
      const slot = this.querySelector('.ods-mobile-navigation__header');
      const header = this.row.closest('hdt-sticky-header');
      // Avoid inheriting the theme's !important padding reset on the header skin.
      slot.className = `${header.className.replace('hdt-header-tranparent-false', '')} ods-mobile-navigation__header`;
      slot.setAttribute('color-scheme', header.getAttribute('color-scheme') || '');
      slot.style.paddingInline = `max(${Math.max(0, rect.left)}px, env(safe-area-inset-left, 0px)) max(${Math.max(0, innerWidth - rect.right)}px, env(safe-area-inset-right, 0px))`;
      if (slot.moveBefore) slot.moveBefore(this.row, null);
      else slot.append(this.row);
      this.querySelector('.ods-mobile-navigation__fallback-close').hidden = true;
      this.row.querySelectorAll('[aria-controls="menu_mobile"]').forEach(button => {
        button.dataset.odsOpenLabel = button.getAttribute('aria-label') || '';
        button.setAttribute('aria-label', this.querySelector('.ods-mobile-navigation__fallback-close').getAttribute('aria-label'));
        button.setAttribute('aria-expanded', 'true');
        button.classList.add('ods-mobile-navigation__close');
      });
    }
    this.reset();
    (this.trigger || this.querySelector('button:not([hidden])'))?.focus({ preventScroll: true });
  }

  closed() {
    this.restoreHeader();
    this.reset();
    if (this.pageScroll !== undefined) window.scrollTo({ top: this.pageScroll, behavior: 'instant' });
    if (!this.handoff && this.trigger?.isConnected && this.mobile.matches) this.trigger.focus({ preventScroll: true });
    this.handoff = false;
  }

  restoreHeader() {
    this.row?.querySelectorAll('[data-ods-open-label]').forEach(button => {
      button.setAttribute('aria-label', button.dataset.odsOpenLabel);
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('ods-mobile-navigation__close');
      delete button.dataset.odsOpenLabel;
    });
    if (this.marker?.parentNode && this.row) {
      const parent = this.marker.parentNode;
      if (parent.moveBefore && this.row.isConnected && parent.isConnected) parent.moveBefore(this.row, this.marker);
      else parent.insertBefore(this.row, this.marker);
      this.marker.remove();
    }
    this.marker = null;
    this.row = null;
  }

  reset() {
    this.cancelPanelMotion();
    this.stack = [];
    this.menu?.querySelectorAll('[data-ods-panel]').forEach(panel => { panel.hidden = true; });
    this.menu?.querySelectorAll('[data-ods-next]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    if (this.root) this.root.hidden = false;
    this.current = this.root;
    if (this.scrollArea) this.scrollArea.scrollTop = 0;
    this.showHelp(true);
  }

  showHelp(show) {
    this.querySelectorAll('.hdt-mobile-other-content, .ods-mobile-navigation__localization').forEach(node => { node.hidden = !show; });
  }

  cancelPanelMotion() {
    this.panelMotion?.cancel();
    this.panelMotion = null;
  }

  animatePanel(back = false) {
    this.cancelPanelMotion();
    if (!this.scrollArea.animate) return;
    // Match the drawer's shared timing, including reduced motion, at every menu level.
    const styles = getComputedStyle(this.dialog);
    const value = styles.getPropertyValue('--ods-overlay-motion-duration').trim();
    const duration = parseFloat(value) * (value.endsWith('ms') ? 1 : 1000);
    const direction = (styles.direction === 'rtl' ? -1 : 1) * (back ? -1 : 1);
    this.panelMotion = this.scrollArea.animate([
      { opacity: 0, transform: `translate3d(${direction * 100}%, 0, 0)` },
      { opacity: 1, transform: 'translate3d(0, 0, 0)' }
    ], {
      duration: Number.isFinite(duration) ? duration : 0,
      easing: styles.getPropertyValue('--ods-overlay-motion-easing').trim() || 'ease'
    });
  }

  navigate(event) {
    const next = event.target.closest('[data-ods-next]');
    if (next) {
      const panel = document.getElementById(next.dataset.odsNext);
      if (!panel || !this.contains(panel)) return;
      this.stack.push({ panel: this.current, trigger: next, scroll: this.scrollArea.scrollTop });
      this.current.hidden = true;
      panel.hidden = false;
      this.current = panel;
      next.setAttribute('aria-expanded', 'true');
      this.scrollArea.scrollTop = 0;
      this.showHelp(false);
      this.animatePanel();
      panel.querySelector('[data-ods-back]')?.focus({ preventScroll: true });
    } else if (event.target.closest('[data-ods-back]')) {
      const previous = this.stack.pop();
      if (!previous) return;
      this.current.hidden = true;
      this.current = previous.panel;
      this.current.hidden = false;
      previous.trigger.setAttribute('aria-expanded', 'false');
      this.showHelp(this.stack.length === 0);
      this.animatePanel(true);
      previous.trigger.focus({ preventScroll: true });
      this.scrollArea.scrollTop = previous.scroll;
    }
  }

  async transition(event) {
    if (!this.dialog.open || this.handoff) return;
    const path = event.composedPath();
    const control = path.find(node => node instanceof Element && node.matches('[aria-controls], shopify-account, a[href]'));
    const inHeader = this.row && path.some(node => node === this.row);
    if (!control || !inHeader || control.getAttribute('aria-controls') === this.dialog.id) return;
    // Close completely before handing the same real control to its existing implementation.
    const activation = path.find(node => node instanceof Element && node.matches('button, a[href]')) || control;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.handoff = true;
    await this.drawer.close(true);
    if (activation.isConnected) activation.click();
  }

  containFocus(event) {
    if (event.key !== 'Tab') return;
    const collect = root => [...root.querySelectorAll('a[href], button, input, select, textarea, [tabindex], shopify-account')].flatMap(node => {
      if (node.shadowRoot) return collect(node.shadowRoot);
      return node.tabIndex >= 0 && !node.disabled && node.getClientRects().length ? [node] : [];
    });
    const controls = collect(this.dialog);
    const first = controls[0];
    const last = controls.at(-1);
    const active = event.composedPath()[0];
    if (event.shiftKey && active === first || !event.shiftKey && active === last) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus({ preventScroll: true });
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.cancelPanelMotion();
    this.restoreHeader();
  }
}

if (!customElements.get('ods-mobile-navigation')) customElements.define('ods-mobile-navigation', ODSMobileNavigation);
